/**
 * callHold — l'appel d'une séance tenu par l'écran du live, et rendu un tour plus tard.
 *
 * L'écran entre dans l'appel à son montage et en sort à son démontage. Un écran démonté puis remonté
 * aussitôt (une clé qui change, une pile qui se remplace) rejouait sinon toute la sortie et toute
 * l'entrée : `left_call` puis `joined_call` au serveur, la salle partagée coupée par le nettoyage du
 * premier montage APRÈS la connexion du second (le `disconnect` du SDK attend la fin du `connect`), et
 * une fausse coupure relevée le temps que la connexion reparte.
 *
 * Ici, l'appel est ouvert une fois par séance et tenu par ses écrans (un compteur, comme
 * `presenceCount`). Le dernier qui le rend ne le ferme pas tout de suite : la fermeture part au tour
 * suivant de la boucle, et un écran de la même séance qui se monte avant la reprend, sans rien
 * rejouer. Une vraie sortie (l'élève quitte, l'app passe ailleurs) ferme l'appel un tour plus tard,
 * avec les mêmes gestes qu'avant.
 *
 * Pur : ni React, ni SDK. L'ouverture et la fermeture sont passées par l'appelant.
 */

/** Ce qu'un écran tient : l'appel ouvert, et s'il l'a repris d'un écran qui venait de le rendre. */
export type HeldCall<Call> = {
    call: Call
    /** Vrai quand l'appel était déjà ouvert pour cette séance : rien n'est à rejouer. */
    resumed: boolean
    /** L'écran rend l'appel ; `close` part au tour suivant si personne ne l'a repris. Une seule fois. */
    release: (close: (call: Call) => void) => void
}

export type CallHold<Call> = {
    /** Un écran de la séance `key` se monte : il reprend l'appel ouvert, ou l'ouvre (`open`). */
    take: (key: number, open: () => Call) => HeldCall<Call>
}

type Entry<Call> = {
    call: Call
    holders: number
    pendingClose: ReturnType<typeof setTimeout> | null
}

export type CallHoldScheduler = {
    later: (run: () => void) => ReturnType<typeof setTimeout>
    cancel: (handle: ReturnType<typeof setTimeout>) => void
}

const NEXT_TURN: CallHoldScheduler = {
    later: (run) => setTimeout(run, 0),
    cancel: (handle) => clearTimeout(handle),
}

export function createCallHold<Call>(scheduler: CallHoldScheduler = NEXT_TURN): CallHold<Call> {
    const entries = new Map<number, Entry<Call>>()

    return {
        take(key, open) {
            const existing = entries.get(key)
            let entry: Entry<Call>
            let resumed = false
            if (existing != null) {
                if (existing.pendingClose != null) scheduler.cancel(existing.pendingClose)
                existing.pendingClose = null
                existing.holders += 1
                entry = existing
                resumed = true
            } else {
                entry = { call: open(), holders: 1, pendingClose: null }
                entries.set(key, entry)
            }

            let released = false
            return {
                call: entry.call,
                resumed,
                release(close) {
                    if (released) return
                    released = true
                    entry.holders = Math.max(0, entry.holders - 1)
                    if (entry.holders > 0) return
                    entry.pendingClose = scheduler.later(() => {
                        entry.pendingClose = null
                        if (entry.holders > 0 || entries.get(key) !== entry) return
                        entries.delete(key)
                        close(entry.call)
                    })
                },
            }
        },
    }
}
